import { isSyntheticBac } from './syntheticBac.js';
import { VARIANCE_ACTIONS, VARIANCE_ACTION_LABELS } from './varianceActions.js';
import {
  bindingFieldsFromContext,
  compactEvidenceSnapshot,
  evidenceOverrideFromContext,
  firstNonBlank,
  isPopcornParentProductCode,
  priceFromContextRow,
} from './varianceActionDialogModel.js';

export const rowKeyFromContext = (context = {}) => firstNonBlank(
  context.sourceRowKey,
  context.rowKey,
  context.row?.sourceRowKey,
  context.row?.rowKey,
  context.row?.storageKey,
  context.row?.fileRowId,
  context.row?.id,
  context.row?.rowNumber,
);

export const buildLinkAction = ({
  context,
  selectedEvidence,
  selectedAccount,
  currentUserName,
  now = new Date().toISOString(),
  idFactory = () => `variance-action-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  accountSelectionMode = '',
}) => {
  if (!context) return null;
  const currentEvidence = evidenceOverrideFromContext(context);
  const bindingFields = bindingFieldsFromContext(context);
  const manualReferenceId = firstNonBlank(selectedEvidence?.referenceId, currentEvidence.referenceId);
  const allowEvidenceAccountFallback = !isPopcornParentProductCode(context.productCode) || Boolean(selectedAccount);
  const salesforceAccountId = firstNonBlank(
    selectedAccount?.salesforceAccountId,
    allowEvidenceAccountFallback ? selectedEvidence?.salesforceAccountId : '',
    allowEvidenceAccountFallback ? selectedEvidence?.row?.salesforceAccountId : '',
    allowEvidenceAccountFallback ? selectedEvidence?.row?.accountId : '',
    allowEvidenceAccountFallback ? bindingFields.salesforceAccountId : '',
  );
  const salesforceAccountName = firstNonBlank(
    selectedAccount?.salesforceAccountName,
    allowEvidenceAccountFallback ? selectedEvidence?.salesforceAccountName : '',
    allowEvidenceAccountFallback ? selectedEvidence?.row?.salesforceAccountName : '',
    allowEvidenceAccountFallback ? selectedEvidence?.row?.accountName : '',
    allowEvidenceAccountFallback ? selectedEvidence?.row?.dealerName : '',
    allowEvidenceAccountFallback ? bindingFields.salesforceAccountName : '',
  );
  const peopleSoftId = firstNonBlank(
    selectedAccount?.peopleSoftId,
    allowEvidenceAccountFallback ? selectedEvidence?.peopleSoftId : '',
    allowEvidenceAccountFallback ? selectedEvidence?.row?.peopleSoftId : '',
    allowEvidenceAccountFallback ? selectedEvidence?.row?.peoplesoftId : '',
    selectedAccount || !allowEvidenceAccountFallback ? '' : bindingFields.peopleSoftId,
  );
  const sourceBac = firstNonBlank(context.row?.bac, context.bac);
  const assignedBac = firstNonBlank(selectedAccount?.bac);
  const isBacAssignment = context.source === 'cpq'
    && assignedBac
    && (!sourceBac || isSyntheticBac(sourceBac));
  const sourceCpqOrderItemId = firstNonBlank(
    context.sourceOrderItemId,
    context.row?.orderItemId,
    context.row?.__orderItemId,
    context.orderItemId,
  );
  const manualOrderItemId = isBacAssignment
    ? sourceCpqOrderItemId
    : firstNonBlank(selectedEvidence?.orderItemId, currentEvidence.orderItemId);
  const productCode = context.productCode;
  return {
    id: idFactory(),
    createdAt: now,
    createdBy: currentUserName,
    actionType: isBacAssignment ? VARIANCE_ACTIONS.ASSIGN_BAC : VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH,
    actionLabel: isBacAssignment
      ? `Assign ${productCode || 'product'} to BAC ${assignedBac}`
      : VARIANCE_ACTION_LABELS[VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH],
    source: isBacAssignment ? context.source : selectedEvidence?.source || context.source,
    sourceLabel: isBacAssignment ? context.sourceLabel : selectedEvidence?.sourceLabel || context.sourceLabel,
    targetSource: context.source,
    targetLabel: context.sourceLabel,
    exportLane: 'none',
    productName: context.productName,
    productCode,
    price: Number(priceFromContextRow(context)) || Number(context.price) || 0,
    productStatus: context.status,
    sourceProductName: selectedEvidence?.productName || '',
    sourceProductCode: selectedEvidence?.productCode || '',
    sourcePrice: selectedEvidence?.price ?? null,
    manualOrderItemId,
    manualReferenceId,
    sourceRowKey: isBacAssignment ? rowKeyFromContext(context) : rowKeyFromContext(selectedEvidence || {}),
    targetRowKey: rowKeyFromContext(context),
    salesforceAccountId,
    salesforceAccountName,
    peopleSoftId,
    bindingMethod: accountSelectionMode === 'manual' || (selectedAccount && !selectedEvidence)
      ? 'manual_account_picker'
      : selectedEvidence
        ? 'manual_picker'
        : 'manual_detail_link',
    bindingConfidence: selectedAccount || selectedEvidence ? 100 : bindingFields.bindingConfidence || 100,
    sourceEvidence: isBacAssignment
      ? compactEvidenceSnapshot(context.row)
      : selectedEvidence ? compactEvidenceSnapshot(selectedEvidence.row) : {},
    targetEvidence: {
      ...compactEvidenceSnapshot(context.row),
      ...(isBacAssignment ? {
        bac: assignedBac,
        assignedBac,
        sourceCpqRowKey: rowKeyFromContext(context),
        salesforceAccountId,
        salesforceAccountName,
        peopleSoftId,
      } : {}),
    },
    problem: isBacAssignment
      ? `CPQ source row ${sourceBac || 'without a BAC'} needs an explicit dealer assignment.`
      : 'Product/account evidence needed a manual link for review.',
    fix: isBacAssignment
      ? `Assign ${productCode || 'product'} to BAC ${assignedBac}`
      : `Force ${context.productName || context.productCode || 'product'} to use selected product/account evidence.`,
    note: '',
    saveStatus: 'draft',
    metadata: {
      createdFrom: 'product_detail_link_modal',
      ...(isBacAssignment ? {
        assignedBac,
        sourceCpqRowKey: rowKeyFromContext(context),
        peopleSoftId,
        sourceSyntheticBac: sourceBac,
        sourceOrderItemId: sourceCpqOrderItemId,
        assignmentScope: 'cpq_row',
        selectedAccount: {
          bac: assignedBac,
          salesforceAccountId,
          salesforceAccountName,
          peopleSoftId,
        },
      } : {}),
    },
  };
};
